import { ArrowLeft, Eye, EyeClosed } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import PasswordRequirementsChecklist from './PasswordRequirementsChecklist';
import styles from './styles.module.scss';

// At least 8 chars, 1 digit, 1 uppercase letter, 1 non-alphanumeric ("unique") character.
const PASSWORD_REGEX = /^(?=.*\d)(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/;

// HORO-10509: the new-password half of ResetPasswordStep, shown once the
// code screen's (ResetPasswordCodeStep) code is actually verified against
// the backend. Mirrors SetPasswordStep's password + confirm-password
// fields/validation exactly, rather than the single-field version this
// screen used before the split.
const ResetPasswordNewPasswordStep = ({ loading, error, isCodeExpired, onBack, onSubmit }) => {
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();
    // HORO-10509: independent show/hide toggles — same pattern as Set
    // password's own password/confirm-password fields.
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        formState: { errors, isValid, touchedFields }
    } = useForm({ mode: 'onChange' });
    const passwordValue = watch('password', '');

    // See SetPasswordStep's identical note — isValid otherwise reports
    // true on mount before the field has been validated.
    useEffect(() => {
        trigger();
    }, [trigger]);

    // Same Roboto 400/16px desktop-card override as the other password
    // screens' field labels.
    const fieldLabelClassName = isDesktop ? `${styles.fieldLabel} ${styles.fieldLabelDesktop}` : styles.fieldLabel;

    return (
        <>
            {/* Back returns to the code screen (local state in
                ResetPasswordStep), not the email-entry step — mainly for
                the case where the verified resetToken expires (10 min,
                see the backend's PASSWORD_RESET_TOKEN_TTL_SEC) while the
                user is still on this screen, since re-verifying is the
                only way forward from there. ResetPasswordStep clears the
                spent code before returning, since going back doesn't
                mean the old code is still good. */}
            <div className={styles.signInHeader}>
                <button type="button" className={styles.backIconBtn} aria-label={t('buttons.back')} onClick={onBack}>
                    <ArrowLeft size={24} color="#FFFFFF" />
                </button>
                <h2 className={`${styles.title} ${styles.titleMixedCase}`}>{t('modal.resetPasswordTitle')}</h2>
                <div className={styles.headerSpacer} aria-hidden="true" />
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <span className={fieldLabelClassName}>{t('modal.newPasswordFieldLabel')}</span>
                <div className={styles.inputWrapper}>
                    <div className={styles.passwordFieldWrap}>
                        <input
                            className={`${styles.fieldInput} ${styles.fieldInputWithToggle}`}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={t('modal.passwordPlaceholder')}
                            {...register('password', { required: true, pattern: PASSWORD_REGEX })}
                        />
                        <button
                            type="button"
                            className={styles.passwordToggleBtn}
                            aria-label={showPassword ? t('buttons.hidePassword') : t('buttons.showPassword')}
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <EyeClosed size={20} color="#FFFFFF" /> : <Eye size={20} color="#FFFFFF" />}
                        </button>
                    </div>
                </div>
                <span className={fieldLabelClassName}>{t('modal.confirmNewPasswordFieldLabel')}</span>
                <div className={styles.inputWrapper}>
                    <div className={styles.passwordFieldWrap}>
                        <input
                            className={`${styles.fieldInput} ${styles.fieldInputWithToggle}`}
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={t('modal.confirmNewPasswordPlaceholder')}
                            {...register('confirmPassword', { required: true, validate: (value) => value === watch('password') })}
                        />
                        <button
                            type="button"
                            className={styles.passwordToggleBtn}
                            aria-label={showConfirmPassword ? t('buttons.hidePassword') : t('buttons.showPassword')}
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                            {showConfirmPassword ? <EyeClosed size={20} color="#FFFFFF" /> : <Eye size={20} color="#FFFFFF" />}
                        </button>
                    </div>
                    {errors.confirmPassword && touchedFields.confirmPassword && (
                        <span className={styles.error}>{t('errors.passwordsMustMatch', 'Passwords must match')}</span>
                    )}
                </div>
                <PasswordRequirementsChecklist password={passwordValue} />
                {/* The code screen already disables its own Continue past
                    expiry, but the countdown keeps ticking underneath
                    while this screen is up too — this covers the case
                    where it expires while the user is still typing here. */}
                {isCodeExpired && <p className={styles.formError}>{t('modal.codeExpired')}</p>}
                {error && <p className={styles.formError}>{error.message}</p>}
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={t('buttons.updatePassword')}
                        loading={loading}
                        disable={loading || !isValid || isCodeExpired}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
        </>
    );
};

export default ResetPasswordNewPasswordStep;
